import { ImmunizationRecord, ImmunizationSearch, ImmunizationSeries } from "../models/Interfaces";
import React, { useEffect, useState } from "react";
import { infoLog } from "../utils/logger";
import { useTranslation } from "react-i18next";
import "./patientCard.css";
import DisclaimerCard from "./Disclaimers/DisclaimerCard";
import RecordCard from "./RecordCard";
import { useNavigate } from "react-router";
import { IconButton } from "./IconButton/IconButton";
import ScreeningResultCard from "./ScreeningResultCard";
import { useAtom } from "jotai";
import { CompleteRecord, izProviderConfigsAtom, refreshingRecordsAtom } from "../jotai/atoms";
import TestResultsCard from "./TestResultsCard";
import getIzProvider from "../models/providers/IzProviders";
import Avatar from "./Avatar";
import { Squirrel } from "./spinner/Squirrel";

interface PatientCardProps {
  index: number;
  completeRecord: CompleteRecord;
  open: boolean;
  onOpenToggle: () => void;
  screening?: boolean;
  hideHeader?: boolean;
}

export default function PatientCard({
  completeRecord,
  index,
  open,
  onOpenToggle,
  screening,
  hideHeader,
}: PatientCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [providerConfigs] = useAtom(izProviderConfigsAtom);
  const [refreshingRecords] = useAtom(refreshingRecordsAtom);
  const providerConfig = providerConfigs.find(
    (config) =>
      config.iz_provider.destination_id.toLowerCase() ===
      completeRecord.record?.izProviderKey.toLowerCase()
  );
  const isRefreshing = refreshingRecords.some(
    (r) => r.attributes.uid === completeRecord.record?.uid
  );

  let izProvider;
  if (completeRecord.record?.izProviderKey) {
    izProvider = getIzProvider(completeRecord.record.izProviderKey, t);
  }

  const selectItemHandler = async (record: ImmunizationRecord) => {
    infoLog(`searchObj ${JSON.stringify(record)}`);
  };

  const goToReports = () => {
    navigate("reports", {
      state: { record: completeRecord.record, back: true, title: "Immunization Reports" },
    });
  };

  const goToAvatars = () => {
    navigate(`${completeRecord.record?.uid}/avatar`, {
      state: {
        backPath: `/home/records#${index}`,
        title: "Select Avatar",
      },
    });
  };

  const goToSHLink = () => {
    navigate(`${completeRecord.record?.uid}/smart-health-link`, {
      state: {
        back: true,
        title: `${completeRecord.record?.firstName} ${completeRecord.record?.lastName}`,
      },
    });
  };

  const ExpandedIcon = () => (
    <div>
      <i className="fa-solid fa-angle-down"></i>
    </div>
  );

  const CollapsedIcon = () => (
    <div>
      <i className="fa-solid fa-angle-up"></i>
    </div>
  );

  const emptyScreening =
    !completeRecord.screening || completeRecord.screening?.results.length === 0;

  const navigateToTestResults = (search: ImmunizationSearch) => {
    navigate("/home/records/screening", {
      state: {
        title: `${search.firstName} ${search.lastName}`,
        back: true,
        searchUid: search.uid,
      },
    });
  };

  return (
    <div className="patient-card flex flex-col gap-1">
      {!hideHeader && (
        <div
          data-testid={`patient-card-${index}`}
          data-open={open}
          className="flex level card-header pt-2 pb-2 gap-2 md:px-10 md:ml-[-40px] md:w-[calc(100%+80px)] max-md:gap-1 max-md:px-3 cursor-pointer md:bg-record-blue-desktop max-md:bg-record-blue border-b border-b-dark-blue"
          onClick={onOpenToggle}
        >
          <div className="md:w-[50px] max-md:w-[48px] shrink-0" onClick={() => goToAvatars()}>
            <Avatar recordId={completeRecord.record?.uid ?? ""} loading={isRefreshing} />
          </div>
          <div
            role="paragraph"
            className="text-xl font-semibold text-white patient-name"
            data-testid="search-card-name"
          >
            {completeRecord.search.isChildSearch
              ? completeRecord.search.childFirstName
              : completeRecord.search.firstName}{" "}
            {completeRecord.search.isChildSearch
              ? completeRecord.search.childLastName
              : completeRecord.search.lastName}
          </div>

          <div className="ml-auto flex max-md:gap-4 md:gap-4">
            {!screening && (
              <>
                <div className="md:hidden flex flex-col">
                  <img
                    className="w-[30px] object-contain"
                    src="/assets/images/Share_180.png"
                    onClick={() => goToReports()}
                    alt={"pdf"}
                  />
                  <p className="text-xs text-white text-center mt-[5px]">PDF</p>
                </div>
                <div className="max-md:hidden my-auto flex">
                  <IconButton onClick={() => goToReports()} icon="pdf" alt={"pdf"} />
                </div>
                {providerConfig?.features?.smart_health_links && (
                  <>
                    <div className="md:hidden flex flex-col">
                      <img
                        className="w-[30px] mt-auto object-contain"
                        src="/assets/images/link.png"
                        onClick={() => goToSHLink()}
                        alt={"link"}
                      />
                      <p className="text-xs text-white mt-[5px]">URL</p>
                    </div>
                    <div className="max-md:hidden my-auto flex">
                      <IconButton onClick={() => goToSHLink()} icon="link" alt={"link"} />
                    </div>
                  </>
                )}
                <div className="my-auto max-md:hidden">|</div>
              </>
            )}
            <div className="my-auto mr-5 max-md:hidden">
              {open ? <ExpandedIcon /> : <CollapsedIcon />}
            </div>
          </div>
        </div>
      )}
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-1 max-md:px-6 mb-4">
          {izProvider?.jurisdictionAbbrev.toLowerCase() === "nj" && !screening && (
            <TestResultsCard onClick={() => navigateToTestResults(completeRecord.search)} />
          )}
          {screening ? (
            <>
              <div className="flex flex-col" key={"disclaimer"}>
                <DisclaimerCard provider={completeRecord.search.izProviderKey} screening />
              </div>
              {emptyScreening ? (
                <ScreeningResultCard key={1} screening={completeRecord.screening!} />
              ) : (
                <div className="flex flex-col" key={index}>
                  {completeRecord.screening?.results.map((screeningResult, index) => (
                    <ScreeningResultCard
                      key={index}
                      screening={completeRecord.screening!}
                      screeningResult={screeningResult}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <DisclaimerCard provider={completeRecord.record!.izProviderKey} />
              {completeRecord.record?.immunizations.map(
                (imm: ImmunizationSeries, index: number) => (
                  <div className="flex flex-col" key={index}>
                    <RecordCard
                      index={index}
                      record={completeRecord.record!}
                      series={imm}
                      onSelect={() => {
                        selectItemHandler(completeRecord.record!);
                      }}
                    />
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
